import React from 'react';
import { CalendarPlus, MessageSquare, PartyPopper } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../ui/sheet';
import { Button } from '../ui/button';

// Format a Date to Google Calendar's YYYYMMDDTHHMMSSZ (UTC) format.
const toGCalDate = (d) => {
  const pad = (n) => String(n).padStart(2, '0');
  return (
    `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}` +
    `T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}Z`
  );
};

// Build the Google Calendar deep-link. Sets a default 24h reminder and a
// 1-hour block so the calendar entry is visible on the day-view.
const buildGCalUrl = (task) => {
  const title = encodeURIComponent(`CircleUpSquad — ${task.title || 'Task'}`);
  const details = encodeURIComponent(
    [
      task.description || '',
      task.pickup ? `From: ${task.pickup}` : '',
      task.dropoff ? `To: ${task.dropoff}` : '',
      task.address ? `Address: ${task.address}` : '',
      task.location ? `Location: ${task.location}` : '',
      task.meal_info ? `Meal: ${task.meal_info}` : '',
      task.creator_name ? `Requested by: ${task.creator_name}` : '',
    ]
      .filter(Boolean)
      .join('\n')
  );
  const location = encodeURIComponent(task.dropoff || task.address || task.location || '');

  const base = 'https://calendar.google.com/calendar/render?action=TEMPLATE';
  if (task.due_date) {
    // Detect a date-only value (e.g., "2026-09-25") vs a full ISO datetime.
    // Google Calendar's all-day event syntax uses YYYYMMDD/YYYYMMDD (end date
    // is exclusive, so add one day). This avoids the previous-day render bug
    // for users west of UTC when the task had no time component.
    const dateOnly = typeof task.due_date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(task.due_date);
    if (dateOnly) {
      const [y, m, d] = task.due_date.split('-');
      const startYMD = `${y}${m}${d}`;
      const endDate = new Date(Date.UTC(Number(y), Number(m) - 1, Number(d) + 1));
      const pad = (n) => String(n).padStart(2, '0');
      const endYMD = `${endDate.getUTCFullYear()}${pad(endDate.getUTCMonth() + 1)}${pad(endDate.getUTCDate())}`;
      return `${base}&text=${title}&dates=${startYMD}/${endYMD}&details=${details}&location=${location}`;
    }
    const start = new Date(task.due_date);
    const end = new Date(start.getTime() + 60 * 60 * 1000);
    return `${base}&text=${title}&dates=${toGCalDate(start)}/${toGCalDate(end)}&details=${details}&location=${location}`;
  }
  return `${base}&text=${title}&details=${details}&location=${location}`;
};

// Build a pre-filled `sms:` compose URL. On iOS/Android this opens Messages
// with the body ready — user picks the recipient (themselves) and hits Send
// so the reminder sits in their thread as a bookmark.
const buildSmsUrl = (task) => {
  const dueStr = task.due_date
    ? new Date(task.due_date).toLocaleString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : 'soon';
  const body = `CircleUpSquad reminder: I said I'd cover "${task.title}" ${task.due_date ? `on ${dueStr}` : ''}${
    task.creator_name ? ` for ${task.creator_name}` : ''
  }.`;
  return `sms:?&body=${encodeURIComponent(body)}`;
};

const TaskClaimedSheet = ({ open, onOpenChange, task }) => {
  if (!task) return null;
  const gcalUrl = buildGCalUrl(task);
  const smsUrl = buildSmsUrl(task);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="rounded-t-3xl" data-testid="task-claimed-sheet">
        <SheetHeader className="text-left">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-100 to-emerald-100 dark:from-cyan-900/40 dark:to-emerald-900/40 flex items-center justify-center">
              <PartyPopper className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <SheetTitle style={{ fontFamily: 'Outfit, sans-serif' }}>You{"'"}re on it!</SheetTitle>
              <SheetDescription className="text-xs">
                Want a nudge before it{"'"}s due? Pick a reminder below.
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <div className="mt-4 rounded-2xl border border-border/60 p-3">
          <p className="text-sm font-semibold text-foreground">{task.title}</p>
          {task.due_date && (
            <p className="text-xs text-muted-foreground mt-0.5">
              Due{' '}
              {new Date(task.due_date).toLocaleDateString(undefined, {
                weekday: 'long',
                month: 'short',
                day: 'numeric',
              })}
            </p>
          )}
        </div>

        <div className="mt-4 space-y-2.5">
          <a
            href={gcalUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 w-full h-12 px-4 rounded-xl bg-cyan-600 hover:bg-cyan-700 text-white font-semibold text-sm transition-colors"
            data-testid="claimed-gcal-btn"
          >
            <CalendarPlus className="w-4 h-4" />
            Add to Google Calendar
          </a>
          <a
            href={smsUrl}
            className="flex items-center gap-3 w-full h-12 px-4 rounded-xl border border-border/60 hover:border-cyan-400 hover:bg-cyan-50/50 dark:hover:bg-cyan-900/10 text-foreground font-semibold text-sm transition-colors"
            data-testid="claimed-sms-btn"
          >
            <MessageSquare className="w-4 h-4 text-cyan-600" />
            Text yourself a reminder
          </a>
          <p className="text-[11px] text-muted-foreground text-center pt-1">
            Google Calendar will send you the notification. The text opens in your Messages app.
          </p>
          <Button
            variant="ghost"
            className="w-full text-xs text-muted-foreground"
            onClick={() => onOpenChange(false)}
            data-testid="claimed-sheet-dismiss"
          >
            No thanks, I{"'"}ve got it
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default TaskClaimedSheet;
